/* tslint:disable:max-line-length variable-name */
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {BehaviorSubject, firstValueFrom, from, of} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {Logger} from '@mydocma/common/src/lib/logger';
import {FieldConfig as FaultFieldConfig} from '@docma-mm-core/field-config/field-config';
import {GetValue, SettingsService} from '../../../../mydocma-settings/settings.service';
import {SettingNamesEnum} from '../../../../mydocma-settings/setting-names.enum';
import {ProjectService} from '@mydocma/project';

@UntilDestroy()
@Injectable()
export class MmFaultFieldMemoryService {
  private memoryValuesLoadingSubject = new BehaviorSubject<boolean>(null);
  public memoryValuesLoading$ = this.memoryValuesLoadingSubject.asObservable();

  private _project: any;
  private _currentMemoryValues: { [scope: string]: any } = null;

  public set currentMemoryValues(value: { [scope: string]: any }) {
    this._currentMemoryValues = value;
  }

  public get currentMemoryValues() {
    return this._currentMemoryValues;
  }

  constructor(private readonly projectService: ProjectService,
              private readonly settingsService: SettingsService,
              private readonly logger: Logger) {

    this.projectService.onCurrentProjectChanged.pipe(untilDestroyed(this))
      .subscribe(project => {
        this._project = null;
        this.currentMemoryValues = null;
      });
  }

  async saveMemoryValues(changedMemoryValues: { [prop: string]: any }, fieldConfigsDict: { [prop: string]: FaultFieldConfig }) {
    if (changedMemoryValues && Object.keys(changedMemoryValues).length > 0) {
      const memoryValues = await this.getMemoryValues(fieldConfigsDict);

      for (const key of Object.keys(changedMemoryValues)) {
        if (changedMemoryValues.hasOwnProperty(key) && fieldConfigsDict.hasOwnProperty(key) && fieldConfigsDict[key].Memory) {
          memoryValues[key] = changedMemoryValues[key];
        } else if (memoryValues?.hasOwnProperty(key)) {
          delete memoryValues[key];
        }
      }

      if (!memoryValues || Object.keys(memoryValues)?.length === 0) {
        await firstValueFrom(this.projectService.currentProject
          .pipe(switchMap(project => this.settingsService.delete(PersonalizationKeysEnum.FaultFieldsMemoryValues, SettingNamesEnum.FaultFieldsMemoryValues, project)
          )));

        this.currentMemoryValues = null;
      } else {
        await firstValueFrom(this.projectService.currentProject
          .pipe(switchMap(project => this.settingsService.save(JSON.stringify(memoryValues), PersonalizationKeysEnum.FaultFieldsMemoryValues, SettingNamesEnum.FaultFieldsMemoryValues, project)
          )));

        this.currentMemoryValues = memoryValues;
      }
    }
  }

  async getMemoryValues(fieldConfigsDict: { [prop: string]: FaultFieldConfig }, initial = true) {
    try {
      let memoryValues: { [scope: string]: any } = {};

      this.memoryValuesLoadingSubject.next(true);

      memoryValues = await firstValueFrom(
        this.projectService.currentProject.pipe(
          switchMap(project => {
            if (this._project?.id === project.id) {
              return of(this.currentMemoryValues ?? {});
            }

            this._project = project;

            return from(
              this.settingsService.load(
                PersonalizationKeysEnum.FaultFieldsMemoryValues,
                SettingNamesEnum.FaultFieldsMemoryValues,
                project
              )
            ).pipe(
              switchMap(value => from(GetValue<{ [scope: string]: any }>(value))),
              map(loaded => {
                const mv: { [scope: string]: any } = {};

                if (loaded) {
                  Object.keys(loaded).forEach(key => {
                    if (fieldConfigsDict.hasOwnProperty(key) && fieldConfigsDict[key].Memory) {
                      mv[key] = loaded[key];
                    }
                  });
                }

                return mv;
              })
            );
          })
        )
      );
      memoryValues = memoryValues ?? {};
      this.currentMemoryValues = memoryValues;
      this.memoryValuesLoadingSubject.next(false);
      return memoryValues;
    } catch (e) {
      this.memoryValuesLoadingSubject.next(false);
      this.logger.error(e, '', 'MmFaultFieldMemoryService');
      return {};
    }


  }

}
